import {formatFileSize, getSafely} from 'shared/web';

import type {CellRendererType, EagColumn} from '../types/EagColumn';
import type {RowData} from '../types/RowData';
import {formatTimeToResponse} from './formatTimeToResponse';
import {getValueFromRawCell} from './getValueFromRawCell';
import {toMetaTextPlainText} from './toMetaTextPlainText';
import {toPlainLabel} from './toPlainLabel';

export type ClipboardCopyMode = 'DATA' | 'WITH_HEADERS' | 'WITH_HEADER_GROUPS';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Reads a single property off an unknown value without assertions (returns undefined for non-objects). */
const readProp = (value: unknown, key: string): unknown =>
  isRecord(value) ? getSafely(value, key) : undefined;

const isPresent = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '';

const joinLabels = (items: unknown): string =>
  (Array.isArray(items) ? items : [items])
    .map((item) => readProp(item, 'label'))
    .filter(isPresent)
    .map(String)
    .join(', ');

const moneyText = (value: unknown): string => {
  const amount = readProp(value, 'amount');
  if (amount === null || amount === undefined) {
    return '';
  }
  const currency = readProp(value, 'currency');
  return isPresent(currency) ? `${String(amount)} ${String(currency)}` : String(amount);
};

const fileTitles = (value: unknown): string =>
  (Array.isArray(value) ? value : [value])
    .map((file) => readProp(file, 'title'))
    .filter(isPresent)
    .map(String)
    .join(', ');

/**
 * Per-renderer-type clipboard text derivation.
 *
 * Object-valued column types (MONEY/TAG/FLAG/FILE/LINK) must be unwrapped to
 * meaningful text — a bare String() on them yields "[object Object]". META_TEXT is
 * string-valued but needs its mention markup collapsed to labels. Module-level
 * formatters (TIME_INTERVAL/FILE_SIZE) are reproduced here so copied text matches the
 * rendered cell. Primitive types (PLAIN/DATE/INTEGER/…) stringify cleanly; their
 * locale-aware grid formatting lives in render-time hooks and is not reproducible
 * here, so raw values are copied as-is.
 *
 * The full (non-Partial) Record over CellRendererType makes a new renderer type a
 * compile error here — every type must state its clipboard behavior explicitly
 * (mirrors CELL_RENDERER_REGISTRY's exhaustiveness).
 */
const CELL_TEXT_BY_TYPE = {
  MONEY: moneyText,
  MONEY_NO_DECIMAL: moneyText,
  TAG: joinLabels,
  // getValueFromRawCell returns the whole raw cell for FLAG; the label(s) live under .value
  FLAG: (value: unknown) => joinLabels(readProp(value, 'value')),
  FILE: fileTitles,
  FILE_PLAIN: fileTitles,
  // LINK cells carry {label, url?} items — singly or as an isArray array;
  // toPlainLabel extracts/joins the labels either way.
  LINK: toPlainLabel,
  // META_TEXT cells are plain strings; mention markup collapses to its label
  // so copied text matches the rendered cell.
  META_TEXT: toMetaTextPlainText,
  TIME_INTERVAL: (value: unknown) =>
    typeof value === 'number' ? formatTimeToResponse(value) : toPlainLabel(value),
  FILE_SIZE: (value: unknown) =>
    typeof value === 'number' ? formatFileSize(value) : toPlainLabel(value),
  // PLAIN-routed and primitive cells: shared with renderCell's fallback so
  // copied text always matches the rendered cell.
  PLAIN: toPlainLabel,
  DATE: toPlainLabel,
  DATE_TIME: toPlainLabel,
  CHECKBOX: toPlainLabel,
  THUMBNAIL: toPlainLabel,
  INTEGER: toPlainLabel,
  ICON: toPlainLabel,
} satisfies Record<CellRendererType, (value: unknown) => string>;

/** Derives the clipboard text for a cell from its raw value. */
const cellText = (row: RowData, column: EagColumn): string => {
  const value = getValueFromRawCell(column, row[column.field]);
  if (value === null || value === undefined) {
    return '';
  }

  return (CELL_TEXT_BY_TYPE[column.type] ?? toPlainLabel)(value);
};

// Spreadsheet TSV uses CSV-style quoting for fields containing delimiters or quotes.
const encodeField = (value: string): string =>
  /[\t\r\n"]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;

/**
 * Serializes rows × visible columns to TSV for the clipboard.
 * - DATA: data rows only.
 * - WITH_HEADERS: column-header row, then data.
 * - WITH_HEADER_GROUPS: column-group row (column.group or ''), then header row, then data.
 * Columns must already be in display order; cells use the same value derivation as GridCell.
 */
export function buildClipboardTsv(
  rows: RowData[],
  columns: EagColumn[],
  mode: ClipboardCopyMode
): string {
  const lines: string[] = [];

  if (mode === 'WITH_HEADER_GROUPS') {
    lines.push(columns.map((column) => encodeField(column.group ?? '')).join('\t'));
  }
  if (mode === 'WITH_HEADERS' || mode === 'WITH_HEADER_GROUPS') {
    lines.push(columns.map((column) => encodeField(column.headerName)).join('\t'));
  }
  for (const row of rows) {
    lines.push(columns.map((column) => encodeField(cellText(row, column))).join('\t'));
  }

  return lines.join('\n');
}
